import { useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from '@/shared/ui/cx';

type FieldShellProps = {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  /** id of the control these labels describe */
  controlId: string;
  hintId: string;
  errorId: string;
  children: ReactNode;
};

function FieldShell({
  label,
  hint,
  error,
  required,
  className,
  controlId,
  hintId,
  errorId,
  children,
}: FieldShellProps) {
  return (
    <div className={cx('field', error && 'field--invalid', className)}>
      {label ? (
        <label className="field__label" htmlFor={controlId}>
          {label}
          {required ? <span aria-hidden> *</span> : null}
        </label>
      ) : null}
      {children}
      {hint && !error ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
      {error ? (
        // role="alert" so a validation message that appears after submit is
        // announced rather than silently rendered.
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Wires up the label/hint/error ids so every control describes itself. */
function useFieldIds(explicitId?: string) {
  const auto = useId();
  const controlId = explicitId ?? auto;
  return { controlId, hintId: `${controlId}-hint`, errorId: `${controlId}-error` };
}

function describedBy(hint: ReactNode, error: ReactNode, hintId: string, errorId: string) {
  if (error) return errorId;
  if (hint) return hintId;
  return undefined;
}

/* ------------------------------------------------------------------------- */

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Rendered inside the field, before the text. */
  iconLeft?: ReactNode;
  fieldClassName?: string;
};

export function Input({
  label,
  hint,
  error,
  iconLeft,
  className,
  fieldClassName,
  id,
  required,
  ...rest
}: InputProps) {
  const { controlId, hintId, errorId } = useFieldIds(id);

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
    >
      <div className={cx('input-wrap', iconLeft && 'input-wrap--with-icon')}>
        {iconLeft ? <span className="input-wrap__icon">{iconLeft}</span> : null}
        <input
          id={controlId}
          className={cx('input', className)}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(hint, error, hintId, errorId)}
          {...rest}
        />
      </div>
    </FieldShell>
  );
}

/* ------------------------------------------------------------------------- */

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  fieldClassName?: string;
};

export function Textarea({
  label,
  hint,
  error,
  className,
  fieldClassName,
  id,
  required,
  ...rest
}: TextareaProps) {
  const { controlId, hintId, errorId } = useFieldIds(id);

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
    >
      <textarea
        id={controlId}
        className={cx('textarea', className)}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(hint, error, hintId, errorId)}
        {...rest}
      />
    </FieldShell>
  );
}

/* ------------------------------------------------------------------------- */

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  fieldClassName?: string;
};

export function Select({
  label,
  hint,
  error,
  className,
  fieldClassName,
  id,
  required,
  children,
  ...rest
}: SelectProps) {
  const { controlId, hintId, errorId } = useFieldIds(id);

  return (
    <FieldShell
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
    >
      <div className="select-wrap">
        <select
          id={controlId}
          className={cx('select', className)}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(hint, error, hintId, errorId)}
          {...rest}
        >
          {children}
        </select>
        <span className="select-wrap__chevron" aria-hidden />
      </div>
    </FieldShell>
  );
}
